/**
 * Asset hotspots for patrol 360° viewer (world-locked on the panorama).
 */

import { resolveCameraHotspotMediaUrl } from "../../../api/cameraHotspotsApi.ts";
import {
  normalizePatrolAssetHotspotShowType,
  resolveHotspotContentUrl,
} from "./patrolAssetHotspotContent";

/** Map Camera management API hotspots → patrol recording `asset_hotspots` field. */
export function mapCameraHotspotsToPatrolAsset(hotspots) {
  if (!Array.isArray(hotspots)) return [];
  return hotspots
    .map((h, index) => {
      if (!h || typeof h !== "object") return null;
      const yaw = Number(h.yaw_deg);
      const pitch = Number(h.pitch_deg);
      if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) return null;

      const rawContent = String(h.content_url ?? "").trim();
      const rawImage = String(h.image_url ?? rawContent).trim();
      const showType = normalizePatrolAssetHotspotShowType(h.show_type, {
        imageUrl: rawImage,
        contentUrl: rawContent,
      });
      const resolvedContent =
        showType === "text" ||
        showType === "camera" ||
        showType === "api_url"
          ? rawContent
          : rawContent.startsWith("http")
            ? rawContent
            : resolveCameraHotspotMediaUrl(rawContent);
      const resolvedImage =
        showType === "text"
          ? rawImage
          : rawImage.startsWith("http")
            ? rawImage
            : resolveCameraHotspotMediaUrl(rawImage);

      return {
        id: String(h.id ?? `hs-${index}`),
        yaw_deg: yaw,
        pitch_deg: pitch,
        pin_direction_deg: Number(h.pin_direction_deg) || 0,
        asset_name: String(h.asset_name ?? "").trim(),
        show_type: showType,
        content_url: resolvedContent,
        image_url: resolvedImage || (showType === "image" ? resolvedContent : ""),
        linked_camera_id: showType === "camera" ? rawContent : "",
      };
    })
    .filter(Boolean);
}

/** Camera management API → camelCase models for 360° overlay (patrol + wankhede-360). */
export function mapCameraHotspotsToAssetHotspotViewModels(hotspots) {
  return mapCameraHotspotsToPatrolAsset(hotspots)
    .map((h) => {
      const showType = h.show_type;
      const contentUrl = String(h.content_url ?? "").trim();
      const imageUrl = String(h.image_url ?? "").trim();
      const linkedCameraId =
        showType === "camera"
          ? String(h.linked_camera_id ?? contentUrl).trim()
          : "";
      return {
        id: h.id,
        yawDeg: h.yaw_deg,
        pitchDeg: h.pitch_deg,
        pinDirectionDeg: Number(h.pin_direction_deg) || 0,
        assetItem: "",
        assetName: String(h.asset_name ?? "").trim(),
        location: "",
        showType,
        contentUrl,
        linkedCameraId,
        imageUrl: imageUrl || (showType === "image" ? contentUrl : ""),
        previewImageUrl: showType === "image" ? contentUrl || imageUrl : imageUrl,
      };
    })
    .filter(
      (h) =>
        Number.isFinite(Number(h.yawDeg)) &&
        Number.isFinite(Number(h.pitchDeg)),
    );
}

export function normalizePatrolAssetHotspots(recording) {
  const raw = recording?.asset_hotspots;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((h, index) => {
      if (h == null || typeof h !== "object") return null;
      const yaw = Number(h.yaw_deg);
      const pitch = Number(h.pitch_deg);
      if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) return null;

      const rawImage =
        h.image_url ?? h.imageUrl ?? h.asset_image ?? h.thumbnail_url ?? "";
      const rawContent =
        h.content_url ??
        h.contentUrl ??
        h.url ??
        h.link_url ??
        h.linkUrl ??
        "";

      const imageUrl = rawImage ? String(rawImage).trim() : "";
      const contentUrl = rawContent ? String(rawContent).trim() : "";
      const showType = normalizePatrolAssetHotspotShowType(
        h.show_type ?? h.showType,
        { imageUrl, contentUrl },
      );
      const resolvedImage =
        showType === "text" || !imageUrl
          ? imageUrl
          : resolveHotspotContentUrl(imageUrl);
      const resolvedContent =
        showType === "text" ||
        showType === "camera" ||
        showType === "api_url"
          ? contentUrl
          : contentUrl
            ? resolveHotspotContentUrl(contentUrl)
            : "";

      const linkedCameraId =
        showType === "camera"
          ? String(
              h.linked_camera_id ??
                h.linkedCameraId ??
                contentUrl ??
                "",
            ).trim()
          : "";

      const openUrl =
        showType === "camera"
          ? linkedCameraId
          : showType === "api_url"
            ? contentUrl
            : resolvedContent ||
              (showType === "image" ? resolvedImage : "") ||
              resolvedImage;

      return {
        id: String(h.id ?? `asset-${index}`),
        yawDeg: yaw,
        pitchDeg: pitch,
        pinDirectionDeg: Number.isFinite(Number(h.pin_direction_deg))
          ? Number(h.pin_direction_deg)
          : 0,
        assetItem: String(h.asset_item ?? h.assetItem ?? "").trim(),
        assetName: String(h.asset_name ?? h.assetName ?? "").trim(),
        location: String(h.location ?? "").trim(),
        showType,
        contentUrl: openUrl,
        linkedCameraId,
        imageUrl: resolvedImage || (showType === "image" ? openUrl : ""),
        previewImageUrl:
          showType === "image"
            ? openUrl
            : resolvedImage || "",
      };
    })
    .filter(Boolean);
}

/** Demo markers for POC / dev when API has no `asset_hotspots`. */
export const PATROL_DEMO_ASSET_HOTSPOTS = [
  {
    id: "doorway",
    yawDeg: -35,
    pitchDeg: 6,
    pinDirectionDeg: 0,
    assetItem: "DR01",
    assetName: "Main doorway",
    location: "Office",
  },
  {
    id: "printer",
    yawDeg: 8,
    pitchDeg: -8,
    pinDirectionDeg: 90,
    assetItem: "PR01",
    assetName: "Office printer",
    location: "Desk A",
  },
  {
    id: "monitor",
    yawDeg: 42,
    pitchDeg: -5,
    pinDirectionDeg: 270,
    assetItem: "MN01",
    assetName: "Workstation monitor",
    location: "Desk B",
  },
];

/** Patrol 360° player — include all configured asset hotspots (including linked cameras). */
export function isPatrolPlayerVisibleHotspot(hotspot) {
  if (!hotspot || typeof hotspot !== "object") return false;
  const yaw = Number(hotspot.yawDeg ?? hotspot.yaw_deg);
  const pitch = Number(hotspot.pitchDeg ?? hotspot.pitch_deg);
  return Number.isFinite(yaw) && Number.isFinite(pitch);
}

export function filterPatrolPlayerAssetHotspots(hotspots) {
  if (!Array.isArray(hotspots)) return [];
  return hotspots.filter(isPatrolPlayerVisibleHotspot);
}

export function hasPatrolAssetHotspots(recording) {
  return filterPatrolPlayerAssetHotspots(
    normalizePatrolAssetHotspots(recording),
  ).length > 0;
}

export function getPatrolAssetHotspotsForRecording(
  recording,
  { useDemoFallback = false } = {},
) {
  const normalized = filterPatrolPlayerAssetHotspots(
    normalizePatrolAssetHotspots(recording),
  );
  if (normalized.length > 0) return normalized;

  if (useDemoFallback) return PATROL_DEMO_ASSET_HOTSPOTS;
  return [];
}
